/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  Input,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ArticleService } from '../../services/article.service';
import { Tag } from '../tag-selector/tag-selector.component';

interface Article {
  id: number | null;
  title: string;
  content: string;
  slug: string;
  tags: string[];
  state?: string;
  current_user_permissions?: {
    destroy: boolean;
  };
}

interface User {
  user_type: string;
}

interface Instruction {
  type: string;
  message: string;
}

interface ItemWrapper {
  data: Article;
}

@Component({
  selector: 'ut-article-form',
  templateUrl: './article-form.component.html',
  styleUrls: ['./article-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ArticleFormComponent implements OnInit, OnDestroy {
  @Input() item?: ItemWrapper;
  @Input() user?: User;

  private destroy$ = new Subject<void>();
  private autoSaveSubject$ = new Subject<void>();

  article: Article = {
    id: null,
    title: '',
    content: '',
    slug: '',
    tags: [],
  };

  currentUser: User = { user_type: '' };
  instructions: Instruction[] = [];
  preselectedTags: Tag[] = [];
  selectedTags: Tag[] = [];

  titleError = '';
  slugError = '';
  contentError = '';

  saveBtnSaved = false;
  autosaveStatus: string | null = null;
  autosaveToggleStatus: string | null = null;
  autosaveError = '';
  autosaveToggleTooltip = '';

  /**
   * FLO-54097: base for the article slug preview. Comes from /api/v1/config
   * (utest_app_base_url, falling back to utest_url) so it follows the app wherever
   * it is served from; this literal is only the value used before config resolves.
   */
  articlesUrlPre = 'https://www.utest.com/articles/';

  constructor(
    private cdr: ChangeDetectorRef,
    private ArticleService: ArticleService,
    @Inject('$state') private $state: any,
    @Inject('StaticValuesService') private StaticValuesService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('ProgressBarService') private ProgressBarService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('ConfigService') private ConfigService: any,
  ) {}

  ngOnInit(): void {
    this.ConfigService.config().then((res: any) => {
      const base = res?.data?.utest_app_base_url || res?.data?.utest_url;
      if (base) {
        this.articlesUrlPre = `${base}/articles/`;
        this.cdr.markForCheck();
      }
    });
    // Get route data from inputs
    if (this.item && this.item.data) {
      this.article = this.item.data;
    }

    if (this.user) {
      this.currentUser = this.user;
    }

    // Add instruction for new articles
    if (!this.article.id) {
      this.instructions.push({
        type: 'warning',
        message: 'When creating an article, please make sure that you are properly <a class="a11y-link" target="_blank" href="https://www.utest.com/articles/2728">citing</a> your sources if you are drawing from previously authored material. Additionally, please note that articles are meant for longer content, and material such as quotes or questions should be left to the forums or posted as a status.',
      });
    }

    // Load available tags
    this.StaticValuesService.tags().then((res: any) => {
      this.preselectedTags = res.data;

      // Set selected tags from item
      if (this.article.tags) {
        this.selectedTags = this.article.tags
          .map((tagTitle) => this.preselectedTags.find((tag) => tag.title === tagTitle))
          .filter((tag) => tag !== undefined) as Tag[];
      }

      this.cdr.markForCheck();
    });

    // Setup auto-save
    this.setAutosaveStatus(null);
    this.setAutosaveTooltip();

    // Setup auto-save subscription
    if (this.article.state !== 'published') {
      this.autoSaveSubject$
        .pipe(
          debounceTime(3000),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.autosavingStart();
        });
    }

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeAlert(index: number): void {
    this.instructions.splice(index, 1);
    this.cdr.markForCheck();
  }

  onTitleChange(): void {
    this.titleError = '';
    this.triggerAutoSave();
  }

  onContentChange(content: string): void {
    this.article.content = content;
    this.contentError = '';
    this.triggerAutoSave();
  }

  onSlugChange(slug: string): void {
    this.article.slug = slug;
    this.slugError = '';
    this.triggerAutoSave();
  }

  onTagsChange(tags: Tag[]): void {
    this.selectedTags = tags;
    this.triggerAutoSave();
  }

  action(actionType: string): void {
    if (!this.validation(actionType === 'autosave')) {
      return;
    }

    // Update tags from selected tags
    this.article.tags = this.selectedTags.map((tag) => tag.title);

    this.ProgressBarService.start();

    const successCallback = (res: any) => {
      this.saveBtnSaved = true;
      this.article.id = res.data.id;

      if (actionType === 'publish') {
        this.ArticleService.publish(this.article.id).then(
          () => {
            this.AlertService.add('Article successfully published');
            this.$state.go('singlearticle', { articleId: this.article.slug });
          },
          (error: any) => {
            this.errorCallback(actionType, error);
          },
        );
      } else if (actionType === 'review') {
        this.ArticleService.review(this.article.id).then(
          () => {
            this.AlertService.add('Article successfully submitted for review');
            this.$state.go('singlearticle', { articleId: this.article.slug });
          },
          (error: any) => {
            this.errorCallback(actionType, error);
          },
        );
      } else if (actionType === 'autosave') {
        this.saveBtnSaved = this.autosaveStatus !== 'disabled';
        this.setAutosaveStatus('success');
        if (this.article.state === 'rejected') {
          this.ArticleService.draft(this.article.id).then((res: any) => {
            this.article = res.data;
            this.cdr.markForCheck();
          });
        }
      } else {
        this.AlertService.add('Article successfully saved');
        if (res.data.state === 'published') {
          this.$state.go('singlearticle', { articleId: res.data.slug });
        }
      }

      this.ProgressBarService.done();
      this.cdr.markForCheck();
    };

    if (this.article.id) {
      this.ArticleService.update(this.article).then(successCallback, (error: any) => {
        this.errorCallback(actionType, error);
      });
    } else {
      this.ArticleService.create(this.article).then(successCallback, (error: any) => {
        this.errorCallback(actionType, error);
      });
    }
  }

  onDelete(): void {
    this.ArticleService.remove(this.article.id).then(
      () => {
        this.$state.go('article');
        this.AlertService.addSuccess('Article deleted.');
      },
      (response: any) => {
        this.AlertService.addDanger(this.ServerMessages.prettyMessage(response));
      },
    );
  }

  toggleAutosave(): void {
    this.autosaveToggleStatus = this.autosaveToggleStatus === 'disabled' ? null : 'disabled';
    this.setAutosaveStatus(this.autosaveToggleStatus);
    this.setAutosaveTooltip();
    this.cdr.markForCheck();
  }

  get isAdmin(): boolean {
    return ['admin', 'moderator'].includes(this.currentUser.user_type);
  }

  get showDeleteBtn(): boolean {
    return !!this.article.id && !!this.article.current_user_permissions?.destroy;
  }

  get isPublished(): boolean {
    return this.article.state === 'published';
  }

  private validation(autosave: boolean): boolean {
    let valid = true;
    let alertMessage = 'Please fill in all required fields';

    if (!this.article.slug) {
      this.slugError = autosave ? '' : 'error';
      this.autosaveError = 'Slug field is empty';
      valid = false;
    }

    if (!this.article.content.trim()) {
      this.contentError = autosave ? '' : 'error';
      this.autosaveError = 'Content field is empty';
      valid = false;
    }

    if (!this.article.title) {
      this.titleError = autosave ? '' : 'error';
      this.autosaveError = 'Title field is empty';
      valid = false;
    }

    if (!this.isAdmin && this.article.content.trim().split(' ').length < 250) {
      this.contentError = autosave ? '' : 'error';
      const msg = 'Article must not contain fewer than 250 words.';
      this.autosaveError = msg;
      alertMessage = msg;
      valid = false;
    }

    if (!valid && !autosave) {
      this.AlertService.add(alertMessage, 'danger');
    }

    return valid;
  }

  private errorCallback(action: string, res: any): void {
    const error = typeof res.data === 'string' ? res.data : res.data.errors[0];
    const autosave = action === 'autosave';

    if (autosave) {
      this.setAutosaveStatus('error');
    }

    if (error.source && error.source.parameter === 'slug') {
      if (!autosave) {
        this.slugError = 'error';
      }
      if (error.title === 'already exists') {
        this.addValidationErrorMessage('An article with this title already exists', autosave);
      } else if (this.article.slug.match(/^[0-9]+$/)) {
        this.addValidationErrorMessage("Slug can't only contain numbers.", autosave);
      } else {
        this.addValidationErrorMessage('Slug can only contain letters, numbers, dashes and underscores.', autosave);
      }
    } else {
      this.addValidationErrorMessage(this.ServerMessages.prettyMessage(res), autosave);
    }

    this.ProgressBarService.done();
    this.cdr.markForCheck();
  }

  private addValidationErrorMessage(text: string, autosave: boolean): void {
    if (autosave) {
      this.autosaveError = text;
    } else {
      this.AlertService.add(text, 'danger');
    }
  }

  private triggerAutoSave(): void {
    this.saveBtnSaved = false;
    if (this.autosaveStatus !== 'disabled' && this.article.state !== 'published') {
      this.setAutosaveStatus(null);
      this.autoSaveSubject$.next();
    }
    this.cdr.markForCheck();
  }

  private autosavingStart(): void {
    if (this.autosaveStatus !== 'disabled') {
      this.action('autosave');
    }
  }

  private setAutosaveStatus(status: string | null): void {
    if (this.autosaveToggleStatus === 'disabled') {
      this.autosaveStatus = 'disabled';
    } else {
      this.autosaveStatus = status;
    }
  }

  private setAutosaveTooltip(): void {
    if (this.autosaveToggleStatus === 'disabled') {
      this.autosaveToggleTooltip = 'Enable autosave';
    } else {
      this.autosaveToggleTooltip = 'Disable autosave';
      // Trigger a save if enabled
      if (this.article.id) {
        this.triggerAutoSave();
      }
    }
  }
}
